import { Component, Event, EventEmitter, h, Prop, State, Watch, Element } from '@stencil/core';
import { Arrow } from '../../icons/index';

type HTMLElementEvent<T extends HTMLElement> = Event & {
  target: T;
};

@Component({
  tag: 'ugc-select',
  styleUrl: 'ugc-select.scss',
  shadow: false,
})
export class UgcSelect {
  @Prop() selectName: string = '';
  @Prop() selectNameTitle: string = '';
  @Prop() value: string | number = '';
  @Prop() data = [];

  @Prop() height: string = '';
  @Prop() gaEventAction: string = '';
  @Prop() gaEventLabel: string = '';
  @Prop() gaEventName: string = '';

  @State() localValue: string | number = '';
  @State() selectResultName: string = '';
  @Event() selectList: EventEmitter;

  @State() isSelectItemOpen: boolean = false;
  @State() isInputActive: boolean = false;
  @State() activeIndex: number = -1;
  // Add a new state to track when dropdown was opened
  private dropdownOpenTime: number = 0;

  @Element() el: HTMLElement;
  selectInput: HTMLInputElement;
  optionsRef: HTMLDivElement;

  selectValueTimer: number;

  componentWillLoad() {
    this.selectValueTimer = window.setInterval(() => {
      this.localValue = this.value;
      const { name } = this.data.find(item => item.id === this.value) || {};
      if (name) {
        this.selectResultName = name;
        window.clearInterval(this.selectValueTimer);
      }
    }, 0);
    window.addEventListener('click', this.handleClickEvent);
    window.addEventListener('keydown', this.handleGlobalKeyDown);
  }

  handleClickEvent = (e: HTMLElementEvent<HTMLTextAreaElement>) => {
    const selectInput = document.getElementById(`${this.selectName}-select`);
    const selectItem = document.getElementById(`${this.selectName}-select-item`);
    const isClickInput = selectInput && selectInput.contains(e.target);
    const isClickSelectItem = selectItem && selectItem.contains(e.target);
    if (isClickInput) {
      this.isSelectItemOpen = !this.isSelectItemOpen;
      if (this.isSelectItemOpen) {
        this.initActiveOption();
        // Set timeout to ensure the DOM is updated before focusing
        setTimeout(() => this.focusActiveOption(), 0);
      }
      return;
    }
    if (isClickSelectItem) {
      this.isInputActive = true;
      this.isSelectItemOpen = !isClickSelectItem;
      return;
    }
    this.isInputActive = false;
    this.isSelectItemOpen = false;
  };

  handleGlobalKeyDown = (e: KeyboardEvent) => {
    // Only handle keyboard events when dropdown is open
    if (!this.isSelectItemOpen) return;

    // Check if focus is within this component
    if (!this.el.contains(document.activeElement)) return;

    this.handleKeyboardNavigation(e);
  };

  handleKeyboardNavigation = (e: KeyboardEvent) => {
    if (!this.isSelectItemOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.navigateOptions(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.navigateOptions(-1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        // Prevent immediate selection if dropdown was just opened
        if (Date.now() - this.dropdownOpenTime < 200) return;

        if (this.activeIndex >= 0 && this.activeIndex < this.data.length) {
          this.handleSelectItem(this.data[this.activeIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.isSelectItemOpen = false;
        if (this.selectInput) {
          this.selectInput.focus();
        }
        break;
      case 'Tab':
        // Let tab navigate naturally but close the dropdown
        this.isSelectItemOpen = false;
        break;
    }
  };

  navigateOptions(direction: number) {
    const optionsCount = this.data.length;
    if (optionsCount === 0) return;

    let newIndex = this.activeIndex + direction;

    // Wrap around
    if (newIndex < 0) {
      newIndex = optionsCount - 1;
    } else if (newIndex >= optionsCount) {
      newIndex = 0;
    }

    this.activeIndex = newIndex;
    this.focusActiveOption();
  }

  initActiveOption() {
    // Set active index to current value or first option
    this.activeIndex = this.data.findIndex(item => item.id === this.value);
    if (this.activeIndex < 0 && this.data.length > 0) {
      this.activeIndex = 0;
    }
    // Record the time when dropdown was opened
    this.dropdownOpenTime = Date.now();
  }

  focusActiveOption() {
    if (this.activeIndex < 0 || !this.optionsRef) return;

    const options = this.optionsRef.querySelectorAll('.select-item');
    if (options && options.length > this.activeIndex) {
      // Just set focus without triggering click event
      setTimeout(() => {
        (options[this.activeIndex] as HTMLElement).focus();
      }, 50); // Small delay to prevent immediate selection
    }
  }

  disconnectedCallback() {
    this.selectValueTimer = null;
    window.clearInterval(this.selectValueTimer);
    window.removeEventListener('click', this.handleClickEvent);
    window.removeEventListener('keydown', this.handleGlobalKeyDown);
  }

  @Watch('value')
  updateLocalValueData() {
    if (this.localValue !== this.value) {
      this.localValue = this.value;
      const { name } = this.data.find(item => item.id === this.value) || {};
      if (name) {
        this.selectResultName = name;
      }
    }
  }

  handleSelectItem(id) {
    this.selectList.emit(id);
    this.isSelectItemOpen = false;
    // Return focus to input when an option is selected
    if (this.selectInput) {
      this.selectInput.focus();
    }
  }

  renderSelect() {
    const isPlaceholderOrigin = this.isSelectItemOpen || this.value !== '';
    const selectName = this.selectNameTitle || this.selectName;
    const selectWrapStyle = this.height === '' ? {} : { height: this.height };
    const listboxId = `${this.selectName}-listbox`;

    return (
      <div class="relative">
        <div
          id={`${this.selectName}-select`}
          class={{
            'ugc-select-wrap': true,
            pointer: true,
            actived: this.value !== '',
            activing: this.isInputActive,
            selecting: this.isSelectItemOpen,
          }}
          style={selectWrapStyle}
        >
          <label htmlFor={this.selectName} id={`${this.selectName}-label`} class="fs-14 pointer">
            {isPlaceholderOrigin ? selectName : this.selectName}
          </label>
          <input
            type="text"
            id={this.selectName}
            class={`fs-14 pointer bg-white plr-16`}
            value={this.selectResultName}
            autocomplete="off"
            readonly
            aria-haspopup="listbox"
            aria-expanded={this.isSelectItemOpen ? 'true' : 'false'}
            aria-labelledby={`${this.selectName}-label`}
            aria-controls={listboxId}
            role="combobox"
            tabindex="0"
            aria-activedescendant={
              this.isSelectItemOpen && this.activeIndex >= 0 ? `${this.selectName}-option-${this.activeIndex}` : null
            }
            ref={el => (this.selectInput = el as HTMLInputElement)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!this.isSelectItemOpen) {
                  this.isSelectItemOpen = true;
                  this.initActiveOption();
                  // Just open the dropdown without immediate selection
                  setTimeout(() => this.focusActiveOption(), 100);
                }
              } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !this.isSelectItemOpen) {
                e.preventDefault();
                this.isSelectItemOpen = true;
                this.initActiveOption();
                setTimeout(() => this.focusActiveOption(), 100);
              } else if (e.key === 'Escape' && this.isSelectItemOpen) {
                e.preventDefault();
                this.isSelectItemOpen = false;
              }
            }}
          />

          <div
            class="arrow-wrap flex-col-center-center"
            style={{ transform: this.isSelectItemOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            aria-hidden="true"
          >
            <Arrow width="24" height="24" />
          </div>
        </div>
        {/* select item */}
        <div
          class={{ 'select-item-wrap': true, selecting: this.isSelectItemOpen }}
          id={listboxId}
          ref={el => (this.optionsRef = el as HTMLDivElement)}
          role="listbox"
          aria-labelledby={`${this.selectName}-label`}
        >
          {this.data.map(({ name, id }, index) => {
            const GAAttributes = this.gaEventName
              ? {
                  'data-ga-event-category': 'ugc',
                  'data-ga-event-action': this.gaEventAction,
                  'data-ga-event-label': `${this.gaEventLabel}=${id}`,
                  'data-ga-event-name': this.gaEventName,
                }
              : null;

            const isSelected = this.value === id;
            const isActive = this.activeIndex === index;

            return (
              <div
                {...GAAttributes}
                key={id}
                data-testid={id}
                id={`${this.selectName}-option-${index}`}
                class={{
                  'select-item': true,
                  block: true,
                  pointer: true,
                  'fs-14': true,
                  'select-item-selected': isSelected,
                  'select-item-active': isActive,
                }}
                onClick={() => this.handleSelectItem(id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleSelectItem(id);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    this.isSelectItemOpen = false;
                    if (this.selectInput) {
                      this.selectInput.focus();
                    }
                  }
                }}
                role="option"
                aria-selected={isSelected ? 'true' : 'false'}
                tabindex="-1"
              >
                {name}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    return [this.renderSelect()];
  }
}
